import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ComponentPreviewRouter } from './components/ComponentPreview';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MerchantProvider } from './contexts/MerchantContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './contexts/ToastContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useEffect } from 'react';
import './lib/i18n'; // Initialize i18next

/*-krisspy-code-start*/
// Auto-generated imports from manifest
import DesignSystemTest from './pages/DesignSystemTest';
import LoginScreen from './pages/LoginScreen';
import DashboardHome from './pages/DashboardHome';
import LoginError from './pages/LoginError';
import ProductsList from './pages/ProductsList';
import InventoryList from './pages/InventoryList';
import BookingsList from './pages/BookingsList';
import AvailabilityCalendar from './pages/AvailabilityCalendar';
import ProductForm from './pages/ProductForm';
import InventoryItemForm from './pages/InventoryItemForm';
import BookingForm from './pages/BookingForm';
import BookingEdit from './pages/BookingEdit';
import NewBookingFlow from './pages/NewBookingFlow';
import QuickBooking from './pages/QuickBooking';
import LocationsList from './pages/LocationsList';
import LocationForm from './pages/LocationForm';
import CategoriesList from './pages/CategoriesList';
import CategoryForm from './pages/CategoryForm';
import DiscountCodesList from './pages/DiscountCodesList';
import DiscountCodeForm from './pages/DiscountCodeForm';
import MerchantSettings from './pages/MerchantSettings';
import EmailSettings from './pages/EmailSettings';
import CancellationEmails from './pages/CancellationEmails';
import FollowUpEmails from './pages/FollowUpEmails';
import SmsMessages from './pages/SmsMessages';
import WhatsappMessages from './pages/WhatsappMessages';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetupPassword from './pages/SetupPassword';
import ReturnReportsList from './pages/ReturnReportsList';
import IssuesPage from './pages/IssuesPage';
import IssueDetailPage from './pages/IssueDetailPage';
import EquipmentCheckPage from './pages/EquipmentCheckPage';
import CustomersList from './pages/CustomersList';
import UsersList from './pages/UsersList';
import Profile from './pages/Profile';
import Analytics from './pages/Analytics';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
/*-krisspy-code-end*/

// Layout wrapper component
function PageWithLayout({ 
  page: Page, 
  layouts = [] 
}: { 
  page: React.ComponentType, 
  layouts?: React.ComponentType<{ children: React.ReactNode }>[] 
}) {
  if (!layouts.length) {
    return <Page />;
  }
  
  // Render nested layouts from outermost to innermost
  return layouts.reduceRight(
    (acc, Layout) => <Layout>{acc}</Layout>, 
    <Page />
  );
}

/**
 * Global guard: redirects to /setup-password whenever an authenticated user
 * has password_set === false, regardless of which route was loaded.
 * Renders nothing — purely a side-effect component.
 */
function PasswordSetupGuard() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      !loading &&
      user &&
      profile &&
      !profile.password_set &&
      location.pathname !== '/setup-password'
    ) {
      navigate('/setup-password', { replace: true });
    }
  }, [loading, user, profile, location.pathname, navigate]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <MerchantProvider>
      <LanguageProvider>
        <ToastProvider>
        <Router>
          <PasswordSetupGuard />
          <Routes>
          {/*-krisspy-code-start*/}
          {/* Auto-generated routes from manifest */}
          {/* Public routes - no authentication required */}
          <Route path="/" element={
            <PageWithLayout page={LoginScreen} layouts={[AuthLayout]} />
          } />

          <Route path="/design-system" element={
            <PageWithLayout page={DesignSystemTest} layouts={[]} />
          } />

          <Route path="/login" element={
            <PageWithLayout page={LoginScreen} layouts={[AuthLayout]} />
          } />

          <Route path="/login/error" element={
            <PageWithLayout page={LoginError} layouts={[AuthLayout]} />
          } />

          <Route path="/forgot-password" element={
            <PageWithLayout page={ForgotPassword} layouts={[AuthLayout]} />
          } />

          <Route path="/reset-password" element={
            <PageWithLayout page={ResetPassword} layouts={[AuthLayout]} />
          } />

          <Route path="/setup-password" element={
            <ProtectedRoute>
              <PageWithLayout page={SetupPassword} layouts={[AuthLayout]} />
            </ProtectedRoute>
          } />

          {/* Protected routes - require authentication */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <PageWithLayout page={DashboardHome} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/analytics" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={Analytics} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/products" element={
            <ProtectedRoute>
              <PageWithLayout page={ProductsList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/inventory" element={
            <ProtectedRoute>
              <PageWithLayout page={InventoryList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/bookings" element={
            <ProtectedRoute>
              <PageWithLayout page={BookingsList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/availability" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={AvailabilityCalendar} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/locations" element={
            <ProtectedRoute>
              <PageWithLayout page={LocationsList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/categories" element={
            <ProtectedRoute>
              <PageWithLayout page={CategoriesList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          {/* Protected routes - require manager role */}
          <Route path="/products/create" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={ProductForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/products/:id/edit" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={ProductForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/inventory/create" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={InventoryItemForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/inventory/:id/edit" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={InventoryItemForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/bookings/create" element={
            <ProtectedRoute requiredRole="operator">
              <PageWithLayout page={BookingForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/bookings/new" element={
            <ProtectedRoute requiredRole="operator">
              <PageWithLayout page={NewBookingFlow} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/bookings/quick" element={
            <ProtectedRoute requiredRole="operator">
              <PageWithLayout page={QuickBooking} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/bookings/:id/edit" element={
            <ProtectedRoute>
              <PageWithLayout page={BookingEdit} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/return-reports" element={
            <ProtectedRoute>
              <PageWithLayout page={ReturnReportsList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/equipment-check" element={
            <ProtectedRoute>
              <PageWithLayout page={EquipmentCheckPage} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/customers" element={
            <ProtectedRoute>
              <PageWithLayout page={CustomersList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/issues" element={
            <ProtectedRoute>
              <PageWithLayout page={IssuesPage} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/issues/:id" element={
            <ProtectedRoute>
              <PageWithLayout page={IssueDetailPage} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/categories/create" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={CategoryForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/categories/:id/edit" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={CategoryForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          {/* Legacy detail route — redirect to edit */}
          <Route path="/categories/:id" element={
            <Navigate to="edit" replace />
          } />

          <Route path="/discount-codes" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={DiscountCodesList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/discount-codes/create" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={DiscountCodeForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/discount-codes/:id/edit" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={DiscountCodeForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/locations/create" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={LocationForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/locations/:id/edit" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={LocationForm} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={MerchantSettings} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/emails" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={EmailSettings} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/cancellation-emails" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={CancellationEmails} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/follow-up-emails" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={FollowUpEmails} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/sms-messages" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={SmsMessages} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/whatsapp-messages" element={
            <ProtectedRoute requiredRole="manager">
              <PageWithLayout page={WhatsappMessages} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/users" element={
            <ProtectedRoute requiredRole="admin">
              <PageWithLayout page={UsersList} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <PageWithLayout page={Profile} layouts={[DashboardLayout]} />
            </ProtectedRoute>
          } />
        {/*-krisspy-code-end*/}
        
        <Route path="/_component/*" element={<ComponentPreviewRouter />} />
          <Route path="*" element={
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h1>404 - Page Not Found</h1>
              <a href="/" style={{ color: 'blue', textDecoration: 'underline' }}>
                Go back to home
              </a>
            </div>
          } />
        </Routes>
        </Router>
        </ToastProvider>
      </LanguageProvider>
      </MerchantProvider>
    </AuthProvider>
  );
}